/**
 * CodeBlockView — NodeView custom pentru code blocks cu:
 *   - dropdown pentru limbaj (12 opțiuni comune + „text" pentru fără highlight)
 *   - buton copy cu feedback
 *   - <code> editabil wrap-uit în NodeViewContent; lowlight aplică automat
 *     clasele hljs pe `.language-X`.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';

// `auto` = lăsăm lowlight să detecteze automat limbajul (highlight.js
// highlightAuto). Internă: storage e null pentru auto, string pentru fix.
const AUTO_VALUE = '__auto__';

// `labelKey` = etichetă tradusă (i18n) la randare; restul sunt nume proprii.
const LANGUAGES: Array<{ value: string; label: string; labelKey?: string }> = [
  { value: AUTO_VALUE, label: 'Auto-detect', labelKey: 'notes_blocks.code.auto_detect' },
  { value: 'plaintext', label: 'Plain text', labelKey: 'notes_blocks.code.plain_text' },
  { value: 'bash', label: 'Bash / Shell' },
  { value: 'cpp', label: 'C / C++' },
  { value: 'css', label: 'CSS' },
  { value: 'go', label: 'Go' },
  { value: 'html', label: 'HTML' },
  { value: 'java', label: 'Java' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'json', label: 'JSON' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'php', label: 'PHP' },
  { value: 'python', label: 'Python' },
  { value: 'rust', label: 'Rust' },
  { value: 'sql', label: 'SQL' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'xml', label: 'XML' },
  { value: 'yaml', label: 'YAML' },
];

export default function CodeBlockView({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) {
  const { t } = useTranslation();
  // `null` în atribut = auto-detect (lowlight highlightAuto). Display: AUTO_VALUE.
  const rawLang = node.attrs.language as string | null | undefined;
  const language = rawLang ?? AUTO_VALUE;
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  const isEditable = editor.isEditable;

  const langLabel = (l: { label: string; labelKey?: string }) =>
    l.labelKey ? t(l.labelKey) : l.label;
  const rawLangEntry = LANGUAGES.find((l) => l.value === rawLang);

  const handleLangChange = (value: string) => {
    // Auto → setăm null ca să trigger highlightAuto. Altfel string-ul ales.
    updateAttributes({ language: value === AUTO_VALUE ? null : value });
  };

  const handleCopy = async () => {
    const text = node.textContent;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        // ignore
      } finally {
        document.body.removeChild(ta);
      }
    }
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <NodeViewWrapper as="div" className="cerbes-code-block my-4">
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border)',
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
        }}
        contentEditable={false}
      >
        {isEditable ? (
          <select
            className="font-mono text-[11px] focus:outline-none cursor-pointer rounded px-2 py-0.5"
            style={{
              color: 'var(--text)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
            }}
            value={language}
            onChange={(e) => handleLangChange(e.target.value)}
            aria-label={t('notes_blocks.code.language')}
            title={t('notes_blocks.code.language_title')}
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {langLabel(l)}
              </option>
            ))}
          </select>
        ) : (
          <span
            className="font-mono text-[11px] uppercase tracking-wider"
            style={{ color: 'var(--text-dim)' }}
          >
            {rawLang
              ? (rawLangEntry ? langLabel(rawLangEntry) : rawLang)
              : t('notes_blocks.code.auto_detect')}
          </span>
        )}
        <button
          type="button"
          onClick={handleCopy}
          className="font-mono text-[11px] px-2 py-0.5 rounded transition-colors"
          style={{ color: copied ? 'var(--accent)' : 'var(--text-muted)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bg-hover)';
            if (!copied) e.currentTarget.style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = copied ? 'var(--accent)' : 'var(--text-muted)';
          }}
          title={t('notes_blocks.code.copy_title')}
        >
          {copied ? t('notes_blocks.code.copied') : t('notes_blocks.code.copy')}
        </button>
      </div>
      <pre
        className="overflow-x-auto m-0"
        style={{
          background: 'var(--bg-elevated)',
          borderLeft: '1px solid var(--border)',
          borderRight: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          padding: '12px 16px',
          fontSize: '13px',
          lineHeight: 1.5,
          fontFamily: "'Geist Mono', ui-monospace, monospace",
        }}
      >
        <NodeViewContent
          as="code"
          className={rawLang ? `language-${rawLang} hljs` : 'hljs'}
        />
      </pre>
    </NodeViewWrapper>
  );
}
